<template>
  <button class="sword-button-wuk" type="button" :class="buttonClasses">
    <sword-icon-wuk v-if="loading" name="spinner" />
    <sword-icon-wuk v-else-if="round && icon" :name="icon" />
    <template v-else>
      <sword-icon-wuk :name="icon" v-if="!!icon && side === 'left'" />
      <span v-if="text">{{ $t(text) }}</span>
      <sword-icon-wuk :name="icon" v-if="!!icon && side === 'right'" />
    </template>
    <slot name="content"></slot>
  </button>
</template>

<script>
import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';

const SIZE_OPTIONS = ['extra-large', 'large', 'medium', 'small'];

const THEME_OPTIONS = [
  'primary',
  'secondary',
  'tertiary',
  'white',
  'transparent',
  'link',
  'text',
  'text-heavy',
  'text-large',
  'text-large-heavy',
  'text-small',
  'text-small-heavy',
];

const SIDE_OPTIONS = ['left', 'right'];

export default {
  name: 'SwordButtonWuk',
  components: { SwordIconWuk },
  props: {
    size: {
      type: String,
      default: 'medium',
      validator(value) {
        return SIZE_OPTIONS.includes(value);
      },
    },
    round: Boolean,
    theme: {
      type: String,
      default: 'primary',
      validator(value) {
        if (value === 'text') {
          console.warn('SwordButtonWuk: "text" theme is deprecated. Use "link" instead.');
          return true;
        }
        return THEME_OPTIONS.includes(value);
      },
    },
    text: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
    },
    side: {
      type: String,
      default: 'right',
      validator(value) {
        return SIDE_OPTIONS.includes(value);
      },
    },
    loading: Boolean,
    fullWidth: Boolean,
    long: Boolean,
    fill: Boolean,
  },
  computed: {
    buttonClasses() {
      return [
        `--${this.theme}`,
        `--${this.size}`,
        { '--round': this.round },
        { '--loading': this.loading },
        { '--full-width': this.fullWidth },
        { '--long': this.long },
        { '--fill': this.fill },
      ];
    },
  },
};
</script>
<style lang="scss" scoped>
.sword-button-wuk {
  @extend %button-base;
  border-radius: $button-border-radius-wuk;

  //types
  &.--primary {
    @extend %button-theme-primary;
  }

  &.--secondary {
    @extend %button-theme-secondary;
  }

  &.--tertiary {
    @extend %button-theme-tertiary;
  }

  &.--white {
    @extend %button-theme-white;
  }

  &.--transparent {
    @extend %button-theme-transparent;
  }

  &.--text,
  &.--link {
    @extend %button-theme-text;
  }

  &.--text-heavy {
    @extend %button-theme-text-medium-heavy;
  }

  &.--text-large {
    @extend %button-theme-text-large;
  }

  &.--text-large-heavy {
    @extend %button-theme-text-large-heavy;
  }

  &.--text-small {
    @extend %button-theme-text-small;
  }

  &.--text-small-heavy {
    @extend %button-theme-text-small-heavy;
  }

  .sword-icon.svg-icon {
    width: $button-round-icon-base-size-wuk;
    height: $button-round-icon-base-size-wuk;
  }

  //sizes
  &.--small {
    @extend %t4;
    height: calc(2 * $button-height-base-wuk);
    border-radius: calc(0.625 * $button-border-radius-wuk);

    &.--round {
      width: calc(2 * $button-height-base-wuk);

      .sword-icon.svg-icon {
        width: calc($button-round-icon-base-size-wuk);
        height: calc($button-round-icon-base-size-wuk);
      }
    }

    &.--long {
      padding: 0 0.5rem;
    }
  }

  &.--medium {
    height: calc(2.5 * $button-height-base-wuk);
    border-radius: calc(0.75 * $button-border-radius-wuk);

    &.--round {
      width: calc(2.5 * $button-height-base-wuk);

      .sword-icon.svg-icon {
        width: calc($button-round-icon-base-size-wuk * 1.25);
        height: calc($button-round-icon-base-size-wuk * 1.25);
      }
    }

    &.--long {
      padding: 0 1rem;
    }
  }

  &.--large {
    height: calc(3 * $button-height-base-wuk);
    border-radius: calc(0.875 * $button-border-radius-wuk);

    &.--round {
      width: calc(3 * $button-height-base-wuk);

      .sword-icon.svg-icon {
        width: calc($button-round-icon-base-size-wuk * 1.25);
        height: calc($button-round-icon-base-size-wuk * 1.5);
      }
    }

    &.--long {
      padding: 0 1rem;
    }
  }

  &.--extra-large {
    height: calc(3.5 * $button-height-base-wuk);
    padding: $button-size-huge-padding-wuk;

    &.--round {
      width: calc(3.5 * $button-height-base-wuk);

      .sword-icon.svg-icon {
        width: calc($button-round-icon-base-size-wuk * 1.25);
        height: calc($button-round-icon-base-size-wuk * 1.25);
      }
    }

    &.--long {
      padding: 0 1rem;
    }
  }

  &.--loading {
    pointer-events: none;

    svg {
      stroke: currentColor;
      fill: none;
    }

    &.--medium {
      .sword-icon.svg-icon {
        width: calc($button-loading-base-size-wuk * 1.1);
        height: calc($button-loading-base-size-wuk * 1.1);
      }
    }

    &.--large {
      .sword-icon.svg-icon {
        width: calc($button-loading-base-size-wuk * 1.5);
        height: calc($button-loading-base-size-wuk * 1.5);
      }
    }

    &.--huge {
      .sword-icon.svg-icon {
        width: calc($button-loading-base-size-wuk * 1.75);
        height: calc($button-loading-base-size-wuk * 1.75);
      }
    }
  }

  &.--full-width {
    width: 100%;
  }

  &.--fill {
    width: 100%;
    height: 100%;
  }

  //round
  &.--round {
    border-radius: 50%;
    min-width: initial;
    padding: 0;
  }
}
</style>
